import { Command, Option } from "clipanion";
import { M8tCommand } from "../../lib/m8t-command.js";
import type {
  CreateBindingRequest,
  CreateBindingResponse,
} from "@m8t-stack/api-contract";
import { apiCall } from "../../lib/api-client.js";
import { LocalCliError } from "../../lib/errors.js";
import { resolveGatewayContext } from "../../lib/gateway-context.js";
import {
  colors,
  renderJson,
  resolveOutputMode,
} from "../../lib/output.js";

export class BindAddCommand extends M8tCommand {
  static paths = [["bind", "add"]];
  static usage = Command.Usage({
    description: "Create a channel→worker binding with bot token storage.",
    details:
      "Stores the bot token in Key Vault and writes a Bindings table row. Returns the webhook URL the channel platform should call. F4-alone smoke fails with 'no_adapter_registered' until F6 (Telegram adapter) ships.",
    examples: [
      [
        "Add a Telegram binding for the cmo worker",
        '$0 bind add telegram --worker cmo --bot-token <from-botfather> --slug cmo-tg --bot-username "@startup_cmo_bot"',
      ],
    ],
  });

  channel = Option.String();
  worker = Option.String("--worker");
  botToken = Option.String("--bot-token");
  slug = Option.String("--slug");
  botUsername = Option.String("--bot-username");
  output = Option.String("--output");
  subscription = Option.String("--subscription", {
    description: "Azure subscription ID to discover gateway in (defaults to active az subscription).",
  });
  resourceGroup = Option.String("--resource-group", {
    description: "m8t-stack resource group to disambiguate the gateway (multi-deployment subscriptions).",
  });

  async executeCommand(): Promise<number> {
    if (!this.worker) {
      throw new LocalCliError({
        code: "USAGE",
        message: "--worker is required.",
        hint: "Example: m8t bind add telegram --worker cmo --bot-token <token> --slug cmo-tg",
      });
    }
    if (!this.botToken) {
      throw new LocalCliError({
        code: "USAGE",
        message: "--bot-token is required.",
        hint: "Example: m8t bind add telegram --worker cmo --bot-token <token> --slug cmo-tg",
      });
    }
    if (!this.slug) {
      throw new LocalCliError({
        code: "USAGE",
        message: "--slug is required.",
        hint: "The slug is the binding's stable id (e.g. cmo-tg). Lowercase letters, digits, hyphens only.",
      });
    }

    const mode = resolveOutputMode(
      this.output as "pretty" | "json" | "auto" | undefined,
      this.context.stdout as NodeJS.WriteStream,
    );
    const interactive = (this.context.stdout as NodeJS.WriteStream).isTTY === true;
    const ctx = await resolveGatewayContext({ interactive, subscriptionId: this.subscription, resourceGroup: this.resourceGroup });

    const body: CreateBindingRequest = {
      channel: this.channel as CreateBindingRequest["channel"],
      bindingId: this.slug,
      agentName: this.worker,
      botToken: this.botToken,
      ...(this.botUsername ? { botUsername: this.botUsername } : {}),
    };

    const data = await apiCall<CreateBindingResponse>(
      { gatewayUrl: ctx.gatewayUrl, gatewayClientId: ctx.gatewayClientId },
      { method: "POST", path: "/api/bindings", body },
      (msg) => this.context.stderr.write(msg + "\n"),
    );

    if (mode === "json") {
      this.context.stdout.write(renderJson(data) + "\n");
      return 0;
    }

    this.context.stdout.write(
      `${colors.success("✓")} created binding ${colors.field(data.binding.bindingId)} (${data.binding.channel} → ${data.binding.agentName}).\n`,
    );
    this.context.stdout.write(`  webhook URL: ${data.webhookUrl}\n`);
    this.context.stdout.write(
      `  ${colors.hint("next step:")} register this URL with the channel platform via the channel-specific adapter (see docs/channels/${data.binding.channel}.md when shipped).\n`,
    );
    return 0;
  }
}
